<template>
  <aside class="rqa_admin_thread" dir="rtl" aria-label="פרטי הפנייה">
    <header class="rqa_admin_thread_header">
      <div class="rqa_admin_thread_title_row">
        <h3 class="rqa_admin_thread_title">{{ title }}</h3>
        <button
          type="button"
          class="button is-small is-light rqa_admin_thread_refresh"
          :class="{ 'is-loading': thread.loading }"
          :disabled="thread.loading"
          aria-label="רענון הפנייה"
          @click="$emit('retry')">רענון</button>
        <button ref="close" type="button" class="delete" aria-label="סגירת הפנייה מהתצוגה" @click="$emit('close')"></button>
      </div>
      <div class="rqa_admin_thread_meta">
        <span class="rqa_muted rqa_small">#{{ thread.rootId }}</span>
        <recruiter-qa-status-pill v-if="ticket && ticket.latest_status" :status="ticket.latest_status" supervisor />
        <span v-if="isClosed" class="tag is-light">סגורה</span>
        <span v-if="ticket && ticket.admin_name" class="rqa_small">יועצת: {{ ticket.admin_name }}</span>
        <span v-if="ticket && ticket.owner_name && ticket.owner_name !== ticket.admin_name" class="rqa_muted rqa_small">(בעמוד של {{ ticket.owner_name }})</span>
        <button
          v-if="canWrite && ticket"
          type="button"
          class="button is-small rqa_admin_lifecycle"
          :class="{ 'is-loading': busy[lifecycleKey] }"
          :disabled="busy[lifecycleKey]"
          @click="toggleLifecycle">{{ isClosed ? 'פתיחה מחדש' : 'סגירת פנייה' }}</button>
      </div>
      <p v-if="opErrors[lifecycleKey]" class="rqa_error rqa_small" role="alert">{{ opErrors[lifecycleKey] }}</p>
      <p v-if="jiraIds.length" class="rqa_small">משימה מקושרת: {{ jiraIds.map(jiraShort).join(', ') }}</p>
    </header>

    <section class="rqa_admin_turns">
      <p v-if="thread.loading && !turns.length" class="rqa_muted rqa_center" role="status">טוענת את הפנייה…</p>
      <div v-if="thread.error" class="rqa_center">
        <p class="rqa_error" role="alert">{{ thread.error }}</p>
        <button type="button" class="button is-small" @click="$emit('retry')">טעינה מחדש</button>
      </div>
      <p v-if="thread.truncated" class="rqa_muted rqa_small">מוצגות 200 ההודעות האחרונות בלבד.</p>

      <div v-for="turn in turns" :key="turn.id" class="rqa_admin_turn" :class="{ rqa_admin_turn_reanswer: reAnswer(turn) }">
        <!-- Private instruction to the system: never shown to the recruiter -->
        <div v-if="isInstruction(turn)" class="rqa_admin_instruction" role="note">
          <div class="rqa_admin_turn_head">
            <span class="rqa_admin_instruction_tag">{{ instructionTag }}</span>
            <span v-if="authorName(turn.author)" class="rqa_small rqa_bold">{{ authorName(turn.author) }}</span>
            <span class="rqa_muted rqa_small">{{ when(turn.created_at) }}</span>
          </div>
          <p class="rqa_admin_note_text">{{ turn.answer }}</p>
        </div>

        <!-- The system's private answer to the supervisor's instruction: never shown to the recruiter -->
        <div v-else-if="isReply(turn)" class="rqa_admin_reply" role="note">
          <div class="rqa_admin_turn_head">
            <span class="rqa_admin_reply_tag">{{ replyTag }}</span>
            <span class="rqa_muted rqa_small">{{ when(turn.created_at) }}</span>
          </div>
          <recruiter-qa-entity-text v-if="turn.answer" text-class="rqa_admin_note_text" :segments="segmentsOf(turn, 'answer')" />
          <recruiter-qa-entity-text v-if="turn.answer_long" text-class="rqa_admin_answer_long" :segments="segmentsOf(turn, 'answer_long')" />
          <recruiter-qa-entity-chips :entities="relatedOf(turn)" />
        </div>

        <!-- Public system message (e.g. a Jira decision outcome) -->
        <div v-else-if="isSystem(turn)" class="rqa_admin_system_note" role="note">
          <div class="rqa_admin_turn_head">
            <span class="rqa_admin_system_tag">{{ systemNoteTag }}</span>
            <span class="rqa_muted rqa_small">{{ when(turn.created_at) }}</span>
          </div>
          <p class="rqa_admin_note_text">{{ turn.answer }}</p>
        </div>

        <!-- Historical management message (no longer sent) -->
        <div v-else-if="isNote(turn)" class="rqa_admin_note" role="note">
          <div class="rqa_admin_turn_head">
            <span class="rqa_admin_note_tag">{{ managementTag }}</span>
            <span v-if="authorName(turn.author)" class="rqa_small rqa_bold">{{ authorName(turn.author) }}</span>
            <span class="rqa_muted rqa_small">{{ when(turn.created_at) }}</span>
          </div>
          <p class="rqa_admin_note_text">{{ turn.answer }}</p>
        </div>

        <template v-else>
          <div class="rqa_admin_turn_head">
            <span v-if="reAnswer(turn)" class="rqa_admin_reanswer_label rqa_small rqa_bold">{{ reAnswerLabel }}</span>
            <recruiter-qa-status-pill v-if="displaced(turn)" status="superseded" supervisor />
            <recruiter-qa-status-pill v-else-if="!reAnswer(turn) || turn.status !== 'answered'" :status="turn.status" supervisor />
            <span class="rqa_muted rqa_small">{{ when(turn.created_at) }}</span>
            <span v-if="turn.jira_task_id && !reAnswer(turn)" class="tag is-light is-small">משימה {{ jiraShort(turn.jira_task_id) }}</span>
          </div>
          <p v-if="!reAnswer(turn)" class="rqa_admin_question">{{ turn.question }}</p>
          <recruiter-qa-attachment v-if="!reAnswer(turn) && turn.attachment" :question-id="turn.id" supervisor />
          <div v-if="hasAnswer(turn)" class="rqa_admin_answer">
            <recruiter-qa-entity-text v-if="turn.answer" text-class="rqa_admin_answer_text" :segments="segmentsOf(turn, 'answer')" />
            <recruiter-qa-entity-text v-if="turn.answer_long" text-class="rqa_admin_answer_long" :segments="segmentsOf(turn, 'answer_long')" />
            <recruiter-qa-entity-chips :entities="relatedOf(turn)" />
            <ul v-if="turn.sources && turn.sources.length" class="rqa_admin_sources">
              <li v-for="(src, i) in turn.sources" :key="i" class="rqa_small">{{ sourceText(src) }}</li>
            </ul>
            <p v-if="attribution(turn)" class="rqa_admin_attribution rqa_small">{{ attribution(turn) }}</p>
            <p v-if="turn.finished_at" class="rqa_muted rqa_small">נענתה: {{ when(turn.finished_at) }}</p>
          </div>
          <p v-else-if="cancelled(turn)" class="rqa_muted rqa_small">{{ cancelledText }}</p>
          <p v-else-if="superseded(turn)" class="rqa_muted rqa_small">הוחלפה בהנחיה או בשאלה חדשה יותר.</p>
          <p v-else-if="turn.status === 'superseded'" class="rqa_muted rqa_small">המענה הוחלף בתשובה מעודכנת.</p>
          <p v-else-if="turn.status === 'failed'" class="rqa_muted rqa_small">המענה נכשל{{ turn.error_code ? ' (' + turn.error_code + ')' : '' }}.</p>
          <p v-else class="rqa_muted rqa_small">עדיין אין תשובה.</p>

          <!-- Linked Jira task: loads independently; one card per task id -->
          <recruiter-qa-jira-card
            v-if="turn.jira_task_id && jiraCardTurn[String(turn.jira_task_id)] === turn.id"
            :question="turn"
            :root-id="thread.rootId" />

          <!-- Per-turn supervisor actions -->
          <div v-if="canWrite && !isPanel(turn)" class="rqa_admin_actions">
            <button v-if="approvable(turn)" type="button" class="button is-small is-primary is-light rqa_act_approve" @click="openPanel('approve', turn)">אשר תשובה בשם המערכת</button>
            <button v-if="answerable(turn)" type="button" class="button is-small rqa_act_answer" @click="openPanel('answer', turn)">ענה בשם המערכת</button>
            <button v-if="cancellable(turn)" type="button" class="button is-small is-white rqa_act_cancel" @click="openPanel('cancel', turn)">ביטול המענה</button>
          </div>

          <!-- Approve the reviewed draft -->
          <div v-if="canWrite && isPanel(turn, 'approve')" class="rqa_admin_panel_box rqa_approve_box">
            <p class="rqa_small rqa_bold">זה מה שיישלח ליועצת:</p>
            <p class="rqa_muted rqa_small rqa_system_voice">{{ systemVoiceText }}</p>
            <div class="rqa_admin_preview">
              <template v-if="approveEdited(turn)">
                <p class="rqa_admin_answer_text">{{ draftOf('approve', turn.id) }}</p>
              </template>
              <template v-else>
                <!-- unedited draft: the server's links are still valid for this exact text -->
                <recruiter-qa-entity-text v-if="turn.answer" text-class="rqa_admin_answer_text" :segments="segmentsOf(turn, 'answer')" />
                <recruiter-qa-entity-text v-if="turn.answer_long" text-class="rqa_admin_answer_long" :segments="segmentsOf(turn, 'answer_long')" />
                <ul v-if="turn.sources && turn.sources.length" class="rqa_admin_sources">
                  <li v-for="(src, i) in turn.sources" :key="i" class="rqa_small">{{ sourceText(src) }}</li>
                </ul>
              </template>
            </div>
            <template v-if="approveEditing">
              <textarea
                class="rqa_admin_input"
                rows="3"
                :maxlength="answerMax"
                :value="draftOf('approve', turn.id)"
                aria-label="עריכת התשובה לפני האישור"
                @input="setDraft('approve', turn.id, $event.target.value)"></textarea>
              <span class="rqa_counter">{{ draftOf('approve', turn.id).length }} / {{ answerMax }}</span>
              <p v-if="approveEdited(turn)" class="rqa_admin_warning rqa_small" role="status">התשובה נערכה: יישלח רק הטקסט הערוך. הפירוט והמקורות לא יישלחו ליועצת.</p>
            </template>
            <div class="rqa_admin_box_actions">
              <button type="button" class="button is-small is-primary rqa_confirm_approve" :class="{ 'is-loading': busyOf('approve', turn.id) }" :disabled="!canConfirmApprove(turn)" @click="confirmApprove(turn)">אשר תשובה בשם המערכת</button>
              <button v-if="!approveEditing" type="button" class="button is-small rqa_edit_approve" @click="startApproveEdit(turn)">עריכת התשובה</button>
              <button type="button" class="button is-small is-white" @click="closePanel">ביטול</button>
            </div>
            <p v-if="errorOf('approve', turn.id)" class="rqa_error rqa_small" role="alert">{{ errorOf('approve', turn.id) }}</p>
          </div>

          <!-- Manual answer -->
          <div v-if="canWrite && isPanel(turn, 'answer')" class="rqa_admin_panel_box rqa_answer_box">
            <p class="rqa_muted rqa_small rqa_system_voice">{{ systemVoiceText }}</p>
            <textarea
              ref="answerInput"
              class="rqa_admin_input"
              rows="3"
              :maxlength="answerMax"
              :value="draftOf('answer', turn.id)"
              placeholder="כתוב את התשובה ליועצת…"
              aria-label="תשובה בשם המערכת"
              @input="setDraft('answer', turn.id, $event.target.value)"></textarea>
            <span class="rqa_counter">{{ draftOf('answer', turn.id).length }} / {{ answerMax }}</span>
            <div class="rqa_admin_box_actions">
              <button type="button" class="button is-small is-primary rqa_confirm_answer" :class="{ 'is-loading': busyOf('answer', turn.id) }" :disabled="!canConfirmAnswer(turn)" @click="confirmAnswer(turn)">שליחת תשובה</button>
              <button type="button" class="button is-small is-white" @click="closePanel">ביטול</button>
            </div>
            <p v-if="errorOf('answer', turn.id)" class="rqa_error rqa_small" role="alert">{{ errorOf('answer', turn.id) }}</p>
          </div>

          <!-- Cancel the pending answer -->
          <div v-if="canWrite && isPanel(turn, 'cancel')" class="rqa_admin_panel_box rqa_cancel_box" role="group" aria-label="אישור ביטול המענה">
            <p class="rqa_small">לבטל את המענה לשאלה זו? היועצת תראה שהמענה לשאלה בוטל.</p>
            <div class="rqa_admin_box_actions">
              <button type="button" class="button is-small is-danger rqa_confirm_cancel" :class="{ 'is-loading': busyOf('cancel', turn.id) }" :disabled="busyOf('cancel', turn.id)" @click="confirmCancel(turn)">ביטול המענה</button>
              <button type="button" class="button is-small is-white" @click="closePanel">חזרה</button>
            </div>
            <p v-if="errorOf('cancel', turn.id)" class="rqa_error rqa_small" role="alert">{{ errorOf('cancel', turn.id) }}</p>
          </div>
        </template>
      </div>
    </section>

    <!-- Instruction to the system (private). Still targets the latest genuine recruiter
         question (expected_question_id), but that question is no longer echoed here:
         supervisors keep writing after the recruiter, so "her last question" read as stale. -->
    <footer v-if="canWrite && !thread.error && thread.rootId" class="rqa_admin_composer" :data-target-id="target ? String(target.id) : null">
      <label class="rqa_admin_composer_label rqa_bold" :for="instructionInputId">הנחיה למערכת</label>
      <p v-if="!target" class="rqa_muted rqa_small rqa_admin_no_target">אין עדיין שאלה של היועצת שאפשר להנחות לגביה.</p>
      <textarea
        :id="instructionInputId"
        class="rqa_admin_input"
        rows="3"
        :maxlength="instructionMax"
        :value="instructionDraft"
        :disabled="!target"
        placeholder="כתוב הנחיה למערכת…"
        @input="setDraft('instruction', null, $event.target.value)"
        @keydown.ctrl.enter.prevent="sendInstruction"
        @keydown.meta.enter.prevent="sendInstruction"></textarea>
      <p class="rqa_muted rqa_small rqa_admin_helper">ההנחיה פנימית ואינה מוצגת ליועצת. המערכת תכין תשובה מעודכנת לפיה.</p>
      <div class="rqa_admin_composer_footer">
        <span class="rqa_counter">{{ instructionDraft.length }} / {{ instructionMax }}</span>
        <button type="button" class="button is-info is-small rqa_send_instruction" :class="{ 'is-loading': busy[instructionKey] }" :disabled="!canSendInstruction" @click="sendInstruction">שלח הנחיה ועדכן תשובה</button>
      </div>
      <p v-if="opErrors[instructionKey]" class="rqa_error rqa_small" role="alert">{{ opErrors[instructionKey] }}</p>
      <p v-else-if="opNotices[instructionKey]" class="rqa_success rqa_small" role="status">{{ opNotices[instructionKey] }}</p>
    </footer>
  </aside>
</template>
<script>
import RecruiterQaStatusPill from './RecruiterQaStatusPill'
import RecruiterQaJiraCard from './RecruiterQaJiraCard'
import RecruiterQaEntityText from './RecruiterQaEntityText'
import RecruiterQaEntityChips from './RecruiterQaEntityChips'
import RecruiterQaAttachment from './RecruiterQaAttachment'
import { rowSegments, unlinkedEntities } from '@/utils/recruiterQaEntities'
import {
  ticketTitle, jiraShortId, dateTime, actorName, isSupervisorNote, isSupervisorInstruction, isSupervisorReply, isSystemNote, answerAttribution,
  isCancelledBySupervisor, isSuperseded, isReAnswer, isGenuineQuestion, groupTurns,
  MANAGEMENT_TAG, INSTRUCTION_TAG, SUPERVISOR_REPLY_TAG, SYSTEM_NOTE_TAG, RE_ANSWER_LABEL, CANCELLED_BY_SUPERVISOR_TEXT
} from '@/utils/recruiterQaFormat'
import { INSTRUCTION_MAX, ANSWER_MAX } from '@/services/recruiterQaApi'
import { opKey, canApprove, canAnswer, canCancel, instructionTarget, isActiveExecution, isDisplacedExecution } from '@/store/modules/recruiterQaAdmin'

const SYSTEM_VOICE_TEXT = 'היועצת תראה את הטקסט כתשובה של המערכת'

// Supervisor thread. Plain-text rendering only ({{ }}). Write controls appear
// only when `canWrite` (GET /config supervisor_writes). Drafts live in the
// store so a stale refresh (409) never loses typed text.
export default {
  name: 'RecruiterQaAdminThread',
  components: { RecruiterQaStatusPill, RecruiterQaJiraCard, RecruiterQaEntityText, RecruiterQaEntityChips, RecruiterQaAttachment },
  props: {
    thread: { type: Object, required: true },
    canWrite: { type: Boolean, default: false }
  },
  data () {
    return {
      panel: null, // { kind: 'approve'|'answer'|'cancel', id }
      approveEditing: false,
      instructionMax: INSTRUCTION_MAX,
      answerMax: ANSWER_MAX,
      managementTag: MANAGEMENT_TAG,
      instructionTag: INSTRUCTION_TAG,
      replyTag: SUPERVISOR_REPLY_TAG,
      systemNoteTag: SYSTEM_NOTE_TAG,
      reAnswerLabel: RE_ANSWER_LABEL,
      systemVoiceText: SYSTEM_VOICE_TEXT,
      cancelledText: CANCELLED_BY_SUPERVISOR_TEXT
    }
  },
  computed: {
    adminState () { return (this.$store && this.$store.state.recruiterQaAdmin) || { drafts: {}, busy: {}, opErrors: {} } },
    drafts () { return this.adminState.drafts || {} },
    busy () { return this.adminState.busy || {} },
    opErrors () { return this.adminState.opErrors || {} },
    opNotices () { return this.adminState.opNotices || {} },
    ticket () { return this.thread.ticket },
    isClosed () { return !!(this.ticket && this.ticket.closed_at) },
    // Re-answer jobs render under their original question.
    turns () { return groupTurns(this.thread.questions || []) },
    title () {
      const fromTicket = this.ticket && this.ticket.title
      const firstQuestion = this.turns.filter(isGenuineQuestion)[0]
      return ticketTitle(fromTicket || (firstQuestion ? firstQuestion.question : '')) || 'פנייה'
    },
    jiraIds () { return (this.ticket && this.ticket.jira_task_ids) || [] },
    // task id -> the latest question turn linked to it (its card carries the decision).
    jiraCardTurn () {
      const map = {}
      this.turns.forEach(t => { if (t && t.jira_task_id && isGenuineQuestion(t)) map[String(t.jira_task_id)] = t.id })
      return map
    },
    instructionKey () { return opKey(this.thread.rootId, 'instruction') },
    instructionInputId () { return 'rqa_instruction_' + this.thread.rootId },
    lifecycleKey () { return opKey(this.thread.rootId, 'lifecycle') },
    instructionDraft () { return this.drafts[this.instructionKey] || '' },
    // The latest genuine recruiter question: the instruction's precondition.
    target () { return instructionTarget(this.thread) },
    canSendInstruction () {
      const len = this.instructionDraft.trim().length
      return !!this.target && !this.busy[this.instructionKey] && len > 0 && this.instructionDraft.length <= INSTRUCTION_MAX
    }
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
    this.$nextTick(() => { if (this.$refs.close) this.$refs.close.focus() })
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return
      const tag = event.target && event.target.tagName
      if (tag === 'TEXTAREA' || tag === 'INPUT') return // never drop a half-typed reply
      if (this.panel) return this.closePanel()
      this.$emit('close')
    },
    when (iso) { return dateTime(iso) },
    jiraShort (id) { return jiraShortId(id) },
    isNote (turn) { return isSupervisorNote(turn) },
    isInstruction (turn) { return isSupervisorInstruction(turn) },
    isReply (turn) { return isSupervisorReply(turn) },
    isSystem (turn) { return isSystemNote(turn) },
    reAnswer (turn) { return isReAnswer(turn) },
    superseded (turn) { return isSuperseded(turn) },
    authorName (actor) { return actorName(actor) },
    attribution (turn) { return answerAttribution(turn) },
    cancelled (turn) { return isCancelledBySupervisor(turn) },
    hasAnswer (turn) { return !!((turn.answer || '').trim() || (turn.answer_long || '').trim()) },
    segmentsOf (turn, field) { return rowSegments(turn, field) },
    relatedOf (turn) { return unlinkedEntities(turn) },
    // Approve only content that is visibly previewed: a needs_human turn with a draft.
    // Only the active execution of the latest question takes actions.
    displaced (turn) { return isDisplacedExecution(this.thread, turn) },
    active (turn) { return isActiveExecution(this.thread, turn) },
    approvable (turn) { return this.active(turn) && canApprove(turn) && this.hasAnswer(turn) },
    answerable (turn) { return this.active(turn) && canAnswer(turn) },
    cancellable (turn) { return this.active(turn) && canCancel(turn) },
    sourceText (src) {
      if (!src) return ''
      if (typeof src === 'string') return src
      return src.label || ((src.type || '') + (src.id ? ' #' + src.id : ''))
    },
    key (kind, id) { return opKey(this.thread.rootId, kind, id) },
    draftOf (kind, id) { return this.drafts[this.key(kind, id)] || '' },
    busyOf (kind, id) { return !!this.busy[this.key(kind, id)] },
    errorOf (kind, id) { return this.opErrors[this.key(kind, id)] || '' },
    setDraft (kind, id, text) {
      this.$store.dispatch('recruiterQaAdmin/setDraft', { key: this.key(kind, id), text })
    },
    isPanel (turn, kind) {
      return !!this.panel && this.panel.id === turn.id && (!kind || this.panel.kind === kind)
    },
    openPanel (kind, turn) {
      this.panel = { kind, id: turn.id }
      this.approveEditing = kind === 'approve' && this.draftOf('approve', turn.id) !== '' && this.approveEditedText(turn)
      if (kind === 'answer') this.$nextTick(() => { const el = this.$refs.answerInput; const node = Array.isArray(el) ? el[0] : el; if (node) node.focus() })
    },
    closePanel () {
      this.panel = null
      this.approveEditing = false
    },
    startApproveEdit (turn) {
      if (!this.draftOf('approve', turn.id)) this.setDraft('approve', turn.id, turn.answer || '')
      this.approveEditing = true
    },
    approveEditedText (turn) {
      return this.draftOf('approve', turn.id).trim() !== String(turn.answer || '').trim()
    },
    // Edited = approve-with-text: the server replaces the answer and drops details/sources.
    approveEdited (turn) { return this.approveEditing && this.approveEditedText(turn) },
    canConfirmApprove (turn) {
      if (this.busyOf('approve', turn.id)) return false
      if (!this.approveEdited(turn)) return true
      const text = this.draftOf('approve', turn.id)
      return text.trim().length > 0 && text.length <= ANSWER_MAX
    },
    canConfirmAnswer (turn) {
      const text = this.draftOf('answer', turn.id)
      return !this.busyOf('answer', turn.id) && text.trim().length > 0 && text.length <= ANSWER_MAX
    },
    async confirmApprove (turn) {
      if (!this.canConfirmApprove(turn)) return
      const params = { id: turn.id }
      if (this.approveEdited(turn)) params.text = this.draftOf('approve', turn.id).trim()
      const res = await this.$store.dispatch('recruiterQaAdmin/approve', params)
      if (res && res.ok) this.closePanel()
    },
    async confirmAnswer (turn) {
      if (!this.canConfirmAnswer(turn)) return
      const res = await this.$store.dispatch('recruiterQaAdmin/answer', { id: turn.id, text: this.draftOf('answer', turn.id).trim() })
      if (res && res.ok) this.closePanel()
    },
    async confirmCancel (turn) {
      const res = await this.$store.dispatch('recruiterQaAdmin/cancel', { id: turn.id })
      if (res && res.ok) this.closePanel()
    },
    sendInstruction () {
      if (!this.canSendInstruction) return
      return this.$store.dispatch('recruiterQaAdmin/sendInstruction', {
        text: this.instructionDraft.trim(),
        expectedQuestionId: this.target.id
      })
    },
    toggleLifecycle () {
      return this.$store.dispatch('recruiterQaAdmin/setLifecycle', { close: !this.isClosed })
    }
  }
}
</script>
<style scoped>
.rqa_admin_thread { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.rqa_admin_thread_header { border-bottom: 1px solid #eee; padding-bottom: 10px; margin-bottom: 10px; }
.rqa_admin_thread_title_row { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.rqa_admin_thread_refresh { margin-inline-start: auto; }
.rqa_admin_thread_title { font-size: 16px; font-weight: 600; margin: 0; word-break: break-word; }
.rqa_admin_thread_meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 6px; }
.rqa_admin_lifecycle { margin-right: auto; }
.rqa_admin_turns { overflow-y: auto; flex: 1; min-height: 0; }
.rqa_admin_turn { border-bottom: 1px solid #f2f2f2; padding: 10px 0; }
.rqa_admin_turn_head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 4px; }
.rqa_admin_question { font-weight: 600; white-space: pre-wrap; word-break: break-word; margin: 0 0 6px; }
.rqa_admin_answer { background: #f7f9fc; border-radius: 6px; padding: 8px 10px; }
.rqa_admin_answer_text, .rqa_admin_answer_long { white-space: pre-wrap; word-break: break-word; margin: 0 0 6px; }
.rqa_admin_answer_long { color: #444; font-size: 13px; }
.rqa_admin_sources { margin: 0 18px 6px 0; list-style: disc; }
.rqa_admin_attribution { color: #5a2d91; margin: 0 0 4px; }
.rqa_admin_note { background: #f4f1fb; border-right: 3px solid #5a2d91; border-radius: 6px; padding: 8px 10px; }
.rqa_admin_note_tag { font-size: 11px; font-weight: bold; color: #fff; background: #5a2d91; border-radius: 4px; padding: 0 6px; }
.rqa_admin_note_text { white-space: pre-wrap; word-break: break-word; margin: 0; }
.rqa_admin_instruction { background: #fff8e6; border-right: 3px solid #b7791f; border-radius: 6px; padding: 8px 10px; }
.rqa_admin_instruction_tag { font-size: 11px; font-weight: bold; color: #fff; background: #b7791f; border-radius: 4px; padding: 0 6px; }
.rqa_admin_reply { background: #eef6f0; border-right: 3px solid #2f855a; border-radius: 6px; padding: 8px 10px; }
.rqa_admin_reply_tag { font-size: 11px; font-weight: bold; color: #fff; background: #2f855a; border-radius: 4px; padding: 0 6px; }
.rqa_admin_system_note { background: #f7f9fc; border-right: 3px solid #3273dc; border-radius: 6px; padding: 8px 10px; }
.rqa_admin_system_tag { font-size: 11px; font-weight: bold; color: #fff; background: #3273dc; border-radius: 4px; padding: 0 6px; }
.rqa_admin_turn_reanswer { border-bottom: 1px solid #f2f2f2; margin-right: 16px; padding-right: 10px; border-right: 2px solid #dbe4f0; }
.rqa_admin_reanswer_label { color: #2a4f80; }
.rqa_system_voice { margin: 0 0 4px; }
.rqa_admin_composer_label { display: block; font-size: 14px; margin-bottom: 4px; }
.rqa_admin_no_target { margin: 0 0 6px; }
.rqa_success { color: #1f6f3f; }
.rqa_admin_actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.rqa_admin_panel_box { border: 1px solid #e3e3e3; border-radius: 6px; padding: 8px 10px; margin-top: 8px; background: #fff; }
.rqa_admin_preview { background: #f7f9fc; border-radius: 6px; padding: 6px 8px; margin: 4px 0 6px; }
.rqa_admin_warning { color: #7a5b00; background: #fff3d6; border-radius: 4px; padding: 4px 6px; margin: 4px 0 0; }
.rqa_admin_box_actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.rqa_admin_input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 8px 10px;
  direction: rtl;
  resize: vertical;
}
.rqa_admin_composer { border-top: 1px solid #eee; padding-top: 10px; margin-top: 6px; }
.rqa_admin_helper { margin: 4px 0 0; }
.rqa_admin_composer_footer { display: flex; align-items: center; justify-content: space-between; margin-top: 6px; }
.rqa_counter { font-size: 11px; color: #888; }
.rqa_bold { font-weight: 600; }
.rqa_muted { color: #777; }
.rqa_small { font-size: 12px; }
.rqa_center { text-align: center; padding: 16px 0; }
.rqa_error { color: #a12620; }
</style>
